import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

import "./DoctorDashboard.css";
import "./PatientHistorySearch.css";
import MedicalDocumentManager from "./MedicalDocumentManager";
import PatientProcedureHistory from "./PatientProcedureHistory";
import "./Procedure.css";
import { API_BASE } from "../apiConfig";

export default function DoctorDashboard() {

    /*
    |--------------------------------------------------------------------------
    | LOGGED-IN DOCTOR
    |--------------------------------------------------------------------------
    |
    | Each doctor must see only appointments assigned to that doctor.
    | Login.jsx stores the logged-in user's ID in localStorage as "userId".
    |
    */

    const doctorId =
        Number(
            localStorage.getItem("userId") || 0
        );

    const [patients, setPatients] = useState([]);

    // Show the loader only for the very first queue load.
    // Background refreshes every 5 seconds are completely silent.
    const [initialLoading, setInitialLoading] =
        useState(true);

    // Prevent overlapping queue requests if the API takes longer than 5 seconds.
    const queueRequestRunning = useRef(false);

    const [selectedPatient, setSelectedPatient] =
        useState(null);

    const [showPatient, setShowPatient] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | LOAD QUEUE
    |--------------------------------------------------------------------------
    */

    const loadPatients = useCallback(
        async ({ showInitialLoader = false } = {}) => {

            // Do not allow two queue requests to run at the same time.
            if (queueRequestRunning.current) {
                return;
            }

            queueRequestRunning.current = true;

            if (showInitialLoader) {
                setInitialLoading(true);
            }

            try {

                if (!doctorId) {
                    throw new Error(
                        "Logged-in Doctor ID not found."
                    );
                }

                const response =
                    await fetch(
                        `${API_BASE}get_waiting_patients.php?doctor_id=${encodeURIComponent(
                            doctorId
                        )}`,
                        {
                            method: "GET",
                            cache: "no-store"
                        }
                    );

                const text =
                    await response.text();

                console.log(
                    "Doctor queue API:",
                    text
                );

                let result;

                try {

                    result =
                        JSON.parse(text);

                } catch {

                    throw new Error(
                        "Doctor queue API returned invalid JSON."
                    );

                }

                if (
                    response.ok &&
                    result.status === "success"
                ) {

                    const queueRows =
                        Array.isArray(result.data)
                            ? result.data
                            : [];

                    /*
                    ----------------------------------------------------------
                    DOCTOR-SPECIFIC QUEUE

                    Primary filtering should happen in
                    get_waiting_patients.php using ?doctor_id=...

                    This frontend check is an additional safeguard when the
                    API response contains doctor_id / assigned_doctor_id.
                    If the API already returns only this doctor's rows, the
                    array is used exactly as returned.
                    ----------------------------------------------------------
                    */

                    const hasDoctorField =
                        queueRows.some(
                            item =>
                                item?.doctor_id !== undefined ||
                                item?.assigned_doctor_id !== undefined
                        );

                    const nextPatients =
                        hasDoctorField
                            ? queueRows.filter(item => {
                                const rowDoctorId =
                                    Number(
                                        item?.doctor_id ??
                                        item?.assigned_doctor_id ??
                                        0
                                    );

                                return rowDoctorId === doctorId;
                            })
                            : queueRows;

                    /*
                    ----------------------------------------------------------
                    IMPORTANT:
                    Keep the existing table visible while refreshing.
                    React updates only after the new data has arrived.
                    ----------------------------------------------------------
                    */
                    setPatients(nextPatients);

                } else {

                    throw new Error(
                        result.msg ||
                        "Unable to load doctor queue."
                    );

                }

            } catch (error) {

                console.error(
                    "Doctor queue error:",
                    error
                );

                /*
                ----------------------------------------------------------
                Only show the popup if the FIRST load fails.
                A temporary background-refresh failure should not disturb
                the doctor or remove the currently displayed patient rows.
                ----------------------------------------------------------
                */
                if (showInitialLoader) {

                    Swal.fire({
                        icon: "error",
                        title: "Unable to load queue",
                        text: error.message
                    });

                }

            } finally {

                queueRequestRunning.current = false;

                if (showInitialLoader) {
                    setInitialLoading(false);
                }

            }

        },
        [doctorId]
    );


    /*
    |--------------------------------------------------------------------------
    | AUTO REFRESH
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        // First load: loader is allowed.
        loadPatients({
            showInitialLoader: true
        });

        // Every 5 seconds: silent refresh.
        // Existing rows stay visible while the request is running.
        const timer =
            setInterval(
                () => {
                    loadPatients({
                        showInitialLoader: false
                    });
                },
                5000
            );

        return () =>
            clearInterval(timer);

    }, [loadPatients]);


    /*
    |--------------------------------------------------------------------------
    | VIEW PATIENT
    |--------------------------------------------------------------------------
    */

    const handleView = async (patient) => {

        try {

            /*
            --------------------------------------------------------
            CHECK PENDING PROCEDURES BEFORE STARTING CONSULTATION
            --------------------------------------------------------
            */

            try {
                const pendingResponse = await fetch(
                    `${API_BASE}check_pending_procedures.php?patient_id=${encodeURIComponent(
                        patient.patient_id
                    )}`
                );

                const pendingText = await pendingResponse.text();

                if (pendingText.trim()) {
                    const pendingResult = JSON.parse(pendingText);

                    if (
                        pendingResponse.ok &&
                        pendingResult.status === "success" &&
                        pendingResult.has_pending
                    ) {
                        const pendingRows = Array.isArray(pendingResult.data)
                            ? pendingResult.data
                            : [];

                        const summary = pendingRows
                            .slice(0, 5)
                            .map(
                                item =>
                                    `<div style="text-align:left;margin:6px 0;">
                                        <strong>${escapePrintHtml(
                                            item.procedure_details || "Procedure"
                                        )}</strong>
                                        <br>
                                        Pending: ${Number(item.pending_count || 0)}
                                        ${item.next_date
                                            ? ` &nbsp; | &nbsp; Next: ${escapePrintHtml(item.next_date)}`
                                            : ""}
                                    </div>`
                            )
                            .join("");

                        const warning = await Swal.fire({
                            icon: "warning",
                            title: "Pending Procedure Available",
                            html:
                                `<strong>This patient has ${Number(
                                    pendingResult.pending_count || 0
                                )} pending procedure day(s).</strong><br><br>` +
                                summary +
                                `<br>You can still continue with today's doctor consultation.`,
                            showCancelButton: true,
                            confirmButtonText: "Continue Consultation",
                            cancelButtonText: "Back",
                            confirmButtonColor: "#198754",
                            allowOutsideClick: false
                        });

                        if (!warning.isConfirmed) {
                            return;
                        }
                    }
                }
            } catch (pendingError) {
                console.error(
                    "Pending procedure check error:",
                    pendingError
                );
                // Do not block consultation if only the warning API fails.
            }

            const data = new FormData();

            data.append(
                "appointment_id",
                String(patient.appointment_id)
            );

            const response = await fetch(
                `${API_BASE}start_consultation.php`,
                {
                    method: "POST",
                    body: data
                }
            );

            const text = await response.text();

            if (!text.trim()) {
                throw new Error(
                    `Empty response from server. HTTP ${response.status}`
                );
            }

            let result;

            try {
                result = JSON.parse(text);
            } catch {
                throw new Error(
                    `Invalid JSON from start_consultation.php: ${text.substring(0, 300)}`
                );
            }

            if (!response.ok || result.status !== "success") {
                throw new Error(
                    result.msg ||
                    result.message ||
                    "Unable to start consultation."
                );
            }

            setSelectedPatient({
                ...patient,
                status: "consulting"
            });

            setShowPatient(true);

            await loadPatients();

        } catch (error) {

            console.error(
                "Start consultation error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Unable to Start Consultation",
                text:
                    error.message ||
                    "Unable to start consultation."
            });

        }

    };


    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const closePatient = () => {

        setShowPatient(false);

        setSelectedPatient(null);

        loadPatients();

    };


    const consultingPatient =
        patients.find(
            patient =>
                patient.status === "consulting"
        ) || null;


    const waitingPatients =
        patients.filter(
            patient =>
                patient.status === "waiting"
        );


    const emergencyPatients =
        waitingPatients.filter(
            patient =>
                patient.priority === "emergency"
        );


    const normalPatients =
        waitingPatients.filter(
            patient =>
                patient.priority !== "emergency"
        );


    return (

        <div className="doctor-dashboard">

            {/* HEADER */}

            <div className="doctor-dashboard-header">

                <div>

                    <h2>
                        <i className="fa fa-user-md"></i>
                        &nbsp; Doctor Dashboard
                    </h2>

                    <p>
                        Patient consultation queue
                    </p>

                </div>


                <div className="queue-count">

                    <strong>
                        {patients.length}
                    </strong>

                    <span>
                        Waiting
                    </span>

                </div>

            </div>


            {/* LOADING */}

            {initialLoading ? (

                <div className="doctor-loading">

                    <div className="doctor-spinner"></div>

                    <span>
                        Loading waiting patients...
                    </span>

                </div>

            ) : (

                <>

                    {/* CURRENTLY WITH DOCTOR */}

                    {consultingPatient && (

                        <section className="queue-section consulting-section">

                            <div className="queue-section-title">

                                <div>

                                    <i className="fa fa-stethoscope"></i>
                                    &nbsp;
                                    Currently With Doctor

                                </div>

                                <span>1</span>

                            </div>

                            <PatientTable
                                patients={[consultingPatient]}
                                onView={handleView}
                                consulting
                            />

                        </section>

                    )}


                    {/* EMERGENCY */}

                    {emergencyPatients.length > 0 && (

                        <section className="queue-section emergency-section">

                            <div className="queue-section-title">

                                <div>

                                    <i className="fa fa-exclamation-triangle"></i>

                                    Emergency Patients

                                </div>

                                <span>
                                    {emergencyPatients.length}
                                </span>

                            </div>


                            <PatientTable
                                patients={
                                    emergencyPatients
                                }
                                onView={
                                    handleView
                                }
                                emergency
                            />

                        </section>

                    )}


                    {/* NORMAL */}

                    <section className="queue-section">

                        <div className="queue-section-title">

                            <div>

                                <i className="fa fa-clock-o"></i>

                                Waiting Patients

                            </div>

                            <span>
                                {normalPatients.length}
                            </span>

                        </div>


                        {normalPatients.length > 0 ? (

                            <PatientTable
                                patients={
                                    normalPatients
                                }
                                onView={
                                    handleView
                                }
                            />

                        ) : (

                            emergencyPatients.length === 0 && (

                                <div className="no-patients">

                                    <i className="fa fa-users"></i>

                                    <h4>
                                        No patients waiting
                                    </h4>

                                    <p>
                                        New appointments will appear here automatically.
                                    </p>

                                </div>

                            )

                        )}

                    </section>

                </>

            )}


            {/* CONSULTATION MODAL */}

            {showPatient &&
                selectedPatient && (

                    <DoctorPatientModal
                        patient={
                            selectedPatient
                        }
                        onClose={
                            closePatient
                        }
                    />

                )}

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| PATIENT TABLE
|--------------------------------------------------------------------------
*/

function PatientTable({
    patients,
    onView,
    emergency = false,
    consulting = false
}) {

    return (

        <div className="doctor-table-wrapper">

            <table className="doctor-table">

                <thead>

                    <tr>

                        <th>
                            #
                        </th>

                        <th>
                            MR ID
                        </th>

                        <th>
                            Patient
                        </th>

                        <th>
                            Phone
                        </th>

                        <th>
                            Arrival
                        </th>

                        <th>
                            Purpose
                        </th>

                        <th>
                            Action
                        </th>

                    </tr>

                </thead>


                <tbody>

                    {patients.map(
                        (patient, index) => (

                            <tr
                                key={
                                    patient.appointment_id
                                }
                                className={
                                    emergency
                                        ? "emergency-row"
                                        : ""
                                }
                            >

                                <td>

                                    <div className="queue-number">

                                        {index + 1}

                                    </div>

                                </td>


                                <td>

                                    <span className="doctor-mr">

                                        {patient.mr_no ||
                                            "-"}

                                    </span>

                                </td>


                                <td>

                                    <div className="doctor-patient">

                                        <div className="doctor-avatar">

                                            <i className="fa fa-user"></i>

                                        </div>

                                        <div>

                                            <strong>
                                                {patient.name}
                                            </strong>

                                            <small>
                                                {patient.age}
                                                {" "}Years /{" "}
                                                {patient.sex}
                                            </small>

                                        </div>

                                    </div>

                                </td>


                                <td>
                                    {patient.phone}
                                </td>


                                <td>

                                    {formatDateTime(
                                        patient.appointment_date
                                    )}

                                </td>


                                <td>

                                    <div className="purpose-text">

                                        {patient.purpose || "-"}

                                    </div>

                                </td>


                                <td>

                                    <button
                                        type="button"
                                        className={
                                            emergency
                                                ? "doctor-view emergency-view"
                                                : "doctor-view"
                                        }
                                        onClick={() =>
                                            onView(
                                                patient
                                            )
                                        }
                                    >

                                        <i
                                            className={
                                                consulting
                                                    ? "fa fa-stethoscope"
                                                    : "fa fa-eye"
                                            }
                                        ></i>
                                            &nbsp;
                                        {consulting
                                            ? "Resume"
                                            : "Consult"}

                                    </button>

                                </td>

                            </tr>

                        )
                    )}

                </tbody>

            </table>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| DATE FORMAT
|--------------------------------------------------------------------------
*/

function escapePrintHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}


/*
|--------------------------------------------------------------------------
| PROFESSIONAL PRESCRIPTION PRINT FORMAT
|--------------------------------------------------------------------------
|
| Doctor can type:
|
| Paracetamol:1 week:1-1-1
| Dolo 650:2 days:1-1-0
|
| This becomes a clean borderless table when printed.
|
| Lines without at least two ":" characters are printed as normal notes.
|
*/

function formatPrescriptionForPrint(value) {
    const raw = String(value || "").trim();

    if (!raw) {
        return "";
    }

    const medicineRows = [];
    const notes = [];

    raw
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .forEach(line => {
            const parts = line
                .split(":")
                .map(part => part.trim());

            if (parts.length >= 3) {
                medicineRows.push({
                    medicine: parts[0] || "-",
                    duration: parts[1] || "-",
                    dosage:
                        parts
                            .slice(2)
                            .join(":")
                            .trim() || "-"
                });
            } else {
                notes.push(line);
            }
        });

    let html = "";

    if (medicineRows.length > 0) {
        html += `
            <div class="medicine-table-wrap">
                <table class="medicine-table">
                    <thead>
                        <tr>
                            <th class="medicine-col">Medicine</th>
                            <th class="duration-col">Duration</th>
                            <th class="dosage-col">Dosage</th>
                        </tr>
                    </thead>

                    <tbody>
                        ${medicineRows
                .map(
                    row => `
                                    <tr>
                                        <td class="medicine-name">
                                            ${escapePrintHtml(row.medicine)}
                                        </td>

                                        <td>
                                            ${escapePrintHtml(row.duration)}
                                        </td>

                                        <td class="dosage-text">
                                            ${escapePrintHtml(row.dosage)}
                                        </td>
                                    </tr>
                                `
                )
                .join("")}
                    </tbody>
                </table>
            </div>
        `;
    }

    if (notes.length > 0) {
        html += `
            <div class="prescription-notes">
                ${notes
                .map(
                    note => `
                            <div class="prescription-note">
                                ${escapePrintHtml(note)}
                            </div>
                        `
                )
                .join("")}
            </div>
        `;
    }

    return html;
}

function doctorPrintSection(label, value, primary = false) {
    if (!value) {
        return "";
    }

    return `
        <div class="section">
            <div class="section-heading">
                ${escapePrintHtml(label)}
            </div>
            <div class="section-content ${primary ? "prescription-content" : ""}">
                ${escapePrintHtml(value)}
            </div>
        </div>
    `;
}

function formatDateTime(value) {
    if (!value) return "-";

    const raw =
        String(value).trim();

    let date;

    if (
        /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/.test(
            raw
        )
    ) {
        date =
            new Date(
                raw.replace(" ", "T") +
                (raw.length === 16
                    ? ":00"
                    : "") +
                "+05:30"
            );
    } else {
        date =
            new Date(raw);
    }

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return (
        new Intl.DateTimeFormat(
            "en-IN",
            {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true
            }
        ).format(date)
    );
}


// function formatDateTime(value) {
//     if (!value) {
//         return "-";
//     }

//     const date = new Date(
//         String(value).replace(" ", "T")
//     );

//     if (Number.isNaN(date.getTime())) {
//         return value;
//     }

//     return date.toLocaleString(
//         "en-IN",
//         {
//             day: "2-digit",
//             month: "short",
//             year: "numeric",
//             hour: "2-digit",
//             minute: "2-digit"
//         }
//     );
// }


/*
|--------------------------------------------------------------------------
| DOCTOR PATIENT MODAL
|--------------------------------------------------------------------------
*/

function DoctorPatientModal({
    patient,
    onClose
}) {

    const [visitInfo, setVisitInfo] = useState({
        purpose: patient.purpose || "",
        bp: patient.bp || "", sugar: patient.sugar || "",
        height: patient.height || "", weight: patient.weight || "",
        temperature: patient.temperature || ""
    });
    const [savingVisitInfo, setSavingVisitInfo] = useState(false);
    const [visitInfoSaved, setVisitInfoSaved] = useState(true);
    const changeVisitInfo = (field, value) => {
        setVisitInfo(prev => ({ ...prev, [field]: value }));
        setVisitInfoSaved(false);
    };
    const saveVisitInfo = async () => {
        if (!visitInfo.purpose.trim()) {
            await Swal.fire({ icon: "warning", title: "Purpose Required", text: "Enter a purpose of visit." });
            return false;
        }
        try {
            setSavingVisitInfo(true);
            const response = await fetch(`${API_BASE}update_appointment_visit_info.php`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ appointment_id: patient.appointment_id, patient_id: patient.patient_id, ...visitInfo })
            });
            const result = await response.json();
            if (!response.ok || result.status !== "success") throw new Error(result.msg || "Unable to update visit info.");
            setVisitInfoSaved(true);
            await Swal.fire({ icon: "success", title: "Visit Details Saved", timer: 1100, showConfirmButton: false });
            return true;
        } catch (error) {
            await Swal.fire({ icon: "error", title: "Unable to Save Visit Details", text: error.message });
            return false;
        } finally { setSavingVisitInfo(false); }
    };

    const [medicalHistory, setMedicalHistory] =
        useState("");

    const [selectedFiles, setSelectedFiles] =
        useState([]);


    const [prescription, setPrescription] =
        useState("");


    const [additionalPrescriptions, setAdditionalPrescriptions] =
        useState([]);


    const [doctorNotes, setDoctorNotes] =
        useState("");


    const [tests, setTests] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | DYNAMIC PROCEDURE PLANS
    |--------------------------------------------------------------------------
    |
    | Example:
    | 3 days + "IM Injection / Dressing" + start date
    | automatically creates Day 1, Day 2, Day 3 after consultation save.
    |
    */

    const [procedurePlans, setProcedurePlans] =
        useState([]);


    const [remarks, setRemarks] =
        useState("");


    const [documents, setDocuments] =
        useState([]);


    const [visits, setVisits] =
        useState([]);


    // Same expandable Visit History used by PatientHistorySearch
    const [expandedVisit, setExpandedVisit] =
        useState(null);

    const [visitPrescriptionData, setVisitPrescriptionData] =
        useState(null);


    const [savingHistory, setSavingHistory] =
        useState(false);


    const [savingConsultation, setSavingConsultation] =
        useState(false);


    const [uploading, setUploading] =
        useState(false);


    const [previewDocument, setPreviewDocument] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | LOAD DATA
    |--------------------------------------------------------------------------
    */

    useEffect(() => {


        loadMedicalHistory();

        loadVisits();

        loadDocuments();

    }, [patient.patient_id]);


    const loadMedicalHistory = async () => {

        try {

            const response =
                await fetch(
                    `${API_BASE}get_patient_history_details.php?patient_id=${patient.patient_id}`
                );

            const result =
                await response.json();

            if (
                result.status === "success"
            ) {

                const rows = Array.isArray(result.data)
                    ? result.data
                    : [];

                setMedicalHistory(
                    rows
                        .map(item => item.detail || "")
                        .filter(Boolean)
                        .join("\n")
                );

            }

        } catch (error) {

            console.error(
                error
            );

        }

    };


    const loadVisits = async () => {

        try {

            const response =
                await fetch(
                    `${API_BASE}get_visit_history.php?patient_id=${patient.patient_id}`
                );

            const result =
                await response.json();

            if (
                result.status === "success"
            ) {

                setVisits(
                    result.data || []
                );

            }

        } catch (error) {

            console.error(
                error
            );

        }

    };


    const loadDocuments = async () => {

        try {

            const response =
                await fetch(
                    `${API_BASE}get_medical_documents.php?patient_id=${patient.patient_id}`
                );

            const result =
                await response.json();

            if (
                result.status === "success"
            ) {

                setDocuments(
                    result.data || []
                );

            }

        } catch (error) {

            console.error(
                error
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | HISTORY INPUT
    |--------------------------------------------------------------------------
    */

    const saveHistory = async () => {

        try {
            setSavingHistory(true);

            const response = await fetch(
                `${API_BASE}update_patient_medical_history.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        patient_id: patient.patient_id,
                        medical_history: medicalHistory || ""
                    })
                }
            );

            const text = await response.text();
            if (!text.trim()) throw new Error(`Empty response. HTTP ${response.status}`);
            const result = JSON.parse(text);
            if (!response.ok || result.status !== "success") {
                throw new Error(result.msg || "Unable to update patient history.");
            }

            await loadMedicalHistory();

            Swal.fire({
                icon: "success",
                title: "History Updated",
                text: "Patient medical history updated successfully.",
                timer: 1300,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire({ icon: "error", title: "History Update Failed", text: error.message });
        } finally {
            setSavingHistory(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | UPLOAD
    |--------------------------------------------------------------------------
    */

    const chooseDocuments = (event) => {
        const files = Array.from(event.target.files || []);
        const allowed = ["doc", "docx", "xls", "xlsx", "pdf", "jpeg", "jpg", "png", "webp"];
        const validFiles = [];

        for (const file of files) {
            const extension = file.name.split(".").pop().toLowerCase();
            if (!allowed.includes(extension)) {
                Swal.fire({ icon: "warning", title: "Invalid File", text: `${file.name} is not supported.` });
                event.target.value = "";
                setSelectedFiles([]);
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({ icon: "warning", title: "File Too Large", text: `${file.name} exceeds 10 MB.` });
                event.target.value = "";
                setSelectedFiles([]);
                return;
            }
            validFiles.push(file);
        }
        setSelectedFiles(validFiles);
    };

    const uploadDocuments = async () => {
        if (!selectedFiles.length) {
            Swal.fire({ icon: "warning", title: "Select Files", text: "Choose one or more medical documents." });
            return;
        }
        try {
            setUploading(true);
            const data = new FormData();
            data.append("patient_id", patient.patient_id);
            data.append("visit_id", "0");
            data.append("appointment_id", patient.appointment_id || "");
            selectedFiles.forEach(file => data.append("files[]", file));

            const response = await fetch(`${API_BASE}upload_medical_documents.php`, { method: "POST", body: data });
            const text = await response.text();
            if (!text.trim()) throw new Error(`Empty response. HTTP ${response.status}`);
            const result = JSON.parse(text);
            if (!response.ok || result.status !== "success") throw new Error(result.msg || "Upload failed.");

            setSelectedFiles([]);
            const input = document.getElementById("doctor-medical-files");
            if (input) input.value = "";
            await loadDocuments();
            Swal.fire({ icon: "success", title: "Uploaded", text: `${result.count || 0} file(s) uploaded.`, timer: 1200, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Upload Error", text: error.message });
        } finally {
            setUploading(false);
        }
    };

    const deleteDocument = async (event, doc) => {
        event.stopPropagation();
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Remove document?",
            text: doc.document_name || "This medical document will be permanently removed.",
            showCancelButton: true,
            confirmButtonText: "Yes, remove",
            confirmButtonColor: "#d33"
        });
        if (!confirm.isConfirmed) return;

        try {
            const response = await fetch(`${API_BASE}delete_medical_document.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ document_id: doc.id, patient_id: patient.patient_id })
            });
            const text = await response.text();
            if (!text.trim()) throw new Error(`Empty response. HTTP ${response.status}`);
            const result = JSON.parse(text);
            if (!response.ok || result.status !== "success") throw new Error(result.msg || "Unable to remove document.");
            setDocuments(prev => prev.filter(item => Number(item.id) !== Number(doc.id)));
            Swal.fire({ icon: "success", title: "Removed", timer: 900, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: error.message });
        }
    };


    /*
    |--------------------------------------------------------------------------
    | DYNAMIC SEPARATE A5 PRESCRIPTIONS / TEST REFERRALS
    |--------------------------------------------------------------------------
    */

    const addAdditionalPrescription = () => {
        setAdditionalPrescriptions(prev => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random()}`,
                title: "",
                content: ""
            }
        ]);
    };


    const updateAdditionalPrescription = (
        id,
        field,
        value
    ) => {
        setAdditionalPrescriptions(prev =>
            prev.map(item =>
                item.id === id
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );
    };


    const removeAdditionalPrescription = (id) => {
        setAdditionalPrescriptions(prev =>
            prev.filter(item => item.id !== id)
        );
    };


    const indiaToday = () =>
        new Intl.DateTimeFormat(
            "en-CA",
            {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "2-digit",
                day: "2-digit"
            }
        ).format(new Date());


    const addProcedurePlan = () => {
        setProcedurePlans(prev => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random()}`,
                total_days: "",
                procedure_details: "",
                start_date: indiaToday()
            }
        ]);
    };


    const updateProcedurePlan = (
        id,
        field,
        value
    ) => {
        setProcedurePlans(prev =>
            prev.map(item =>
                item.id === id
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );
    };


    const removeProcedurePlan = (id) => {
        setProcedurePlans(prev =>
            prev.filter(item => item.id !== id)
        );
    };


    const getProcedurePayload = () =>
        procedurePlans
            .filter(item =>
                String(item.total_days || "").trim() ||
                String(item.procedure_details || "").trim() ||
                String(item.start_date || "").trim()
            )
            .map(item => ({
                total_days: Number(item.total_days || 0),
                procedure_details:
                    String(item.procedure_details || "").trim(),
                start_date:
                    String(item.start_date || "").trim()
            }));


    const getPrintableAdditionalPrescriptions = () =>
        additionalPrescriptions
            .map((item, index) => ({
                ...item,
                title:
                    String(item.title || "").trim() ||
                    `Additional Prescription ${index + 1}`,
                content:
                    String(item.content || "").trim()
            }))
            .filter(item => item.content);


    /*
    |--------------------------------------------------------------------------
    | PRINT CURRENT PRESCRIPTION - A5
    |--------------------------------------------------------------------------
    */

    const printCurrentPrescription = (
        pageSelection = "all"
    ) => {

        const extraPages =
            getPrintableAdditionalPrescriptions();

        const pages = [];

        if (String(prescription || "").trim()) {
            pages.push({
                key: "main",
                title: "Prescription",
                content: String(prescription).trim()
            });
        }

        extraPages.forEach((item, index) => {
            pages.push({
                key: `extra-${index}`,
                title:
                    item.title ||
                    `Additional Prescription ${index + 1}`,
                content: item.content
            });
        });

        let pagesToPrint = pages;

        if (pageSelection !== "all") {
            pagesToPrint =
                pages.filter(
                    item => item.key === pageSelection
                );
        }

        if (!pagesToPrint.length) {
            Swal.fire({
                icon: "info",
                title: "Nothing to Print",
                text:
                    "Enter the main prescription or at least one additional prescription / test referral."
            });
            return;
        }

        const printWindow = window.open(
            "",
            "_blank",
            "width=800,height=900"
        );

        if (!printWindow) {
            Swal.fire({
                icon: "warning",
                title: "Popup Blocked",
                text:
                    "Please allow popups in your browser to print the prescription."
            });
            return;
        }

        const visitDate =
            new Date().toLocaleString(
                "en-IN",
                {
                    timeZone: "Asia/Kolkata",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true
                }
            ) + " IST";

        const sheets = pagesToPrint
            .map(page => `
                <section class="a5-page">
                    <div class="prescription-header">
                        <h1>Kolath Medical Centre</h1>
                        <p>${escapePrintHtml(page.title)}</p>
                    </div>

                    <div class="patient-grid">
                        <div>
                            <strong>MR ID:</strong>
                            ${escapePrintHtml(patient.mr_no || "-")}
                        </div>

                        <div>
                            <strong>Date:</strong>
                            ${escapePrintHtml(visitDate)}
                        </div>

                        <div>
                            <strong>Name:</strong>
                            ${escapePrintHtml(patient.name || "-")}
                        </div>

                        <div>
                            <strong>Phone:</strong>
                            ${escapePrintHtml(patient.phone || "-")}
                        </div>

                        <div>
                            <strong>Age:</strong>
                            ${escapePrintHtml(patient.age || "-")}
                        </div>

                        <div>
                            <strong>Gender:</strong>
                            ${escapePrintHtml(patient.sex || "-")}
                        </div>
                    </div>

                    <div class="page-title">
                        ${escapePrintHtml(page.title)}
                    </div>

                    <div class="prescription-content">
                        ${formatPrescriptionForPrint(page.content)}
                    </div>

                    <div class="doctor-signature">
                        Doctor Signature
                    </div>
                </section>
            `)
            .join("");

        const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Prescription - ${escapePrintHtml(patient.mr_no || "")}</title>

<style>
    @page {
        size: A5 portrait;
        margin: 10mm;
    }

    * {
        box-sizing: border-box;
    }

    html,
    body {
        margin: 0;
        padding: 0;
        background: #fff;
        color: #111;
        font-family: Arial, Helvetica, sans-serif;
    }

    .a5-page {
        width: 128mm;
        min-height: 190mm;
        margin: 0 auto;
        padding: 2mm 3mm;
    }

    .a5-page:not(:last-child) {
        page-break-after: always;
        break-after: page;
    }

    .prescription-header {
        text-align: center;
        border-bottom: 2px solid #222;
        padding-bottom: 8px;
        margin-bottom: 10px;
    }

    .prescription-header h1 {
        margin: 0;
        font-size: 20px;
    }

    .prescription-header p {
        margin: 4px 0 0;
        font-size: 12px;
        font-weight: 700;
    }

    .patient-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 5px 14px;
        font-size: 12px;
        border-bottom: 1px solid #ccc;
        padding-bottom: 9px;
        margin-bottom: 12px;
    }

    .page-title {
        margin: 12px 0 14px;
        padding-bottom: 6px;
        text-align: center;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        border-bottom: 1px solid #bbb;
    }

    .prescription-content {
        min-height: 100mm;
        font-size: 13px;
        line-height: 1.55;
    }

    .medicine-table-wrap {
        width: 100%;
        margin-top: 4px;
    }

    .medicine-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        border: none;
    }

    .medicine-table th,
    .medicine-table td {
        border: none !important;
        padding: 6px 5px;
        text-align: left;
        vertical-align: top;
        word-break: break-word;
    }

    .medicine-table thead th {
        padding-top: 2px;
        padding-bottom: 7px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        color: #444;
    }

    .medicine-table tbody td {
        font-size: 13px;
        line-height: 1.45;
    }

    .medicine-col {
        width: 52%;
    }

    .duration-col {
        width: 22%;
    }

    .dosage-col {
        width: 26%;
    }

    .medicine-name {
        font-weight: 600;
        padding-left: 0 !important;
    }

    .dosage-text {
        white-space: nowrap;
        font-weight: 600;
        letter-spacing: 0.2px;
    }

    .prescription-notes {
        margin-top: 14px;
        padding-top: 8px;
        border-top: 1px solid #ddd;
    }

    .prescription-note {
        margin: 4px 0;
        font-size: 12.5px;
        line-height: 1.5;
    }

    .doctor-signature {
        margin-top: 15mm;
        text-align: right;
        font-size: 11px;
    }

    @media print {
        .a5-page {
            width: auto;
            min-height: auto;
            margin: 0;
        }
    }
</style>
</head>

<body>
    ${sheets}

<script>
    window.onafterprint = function () {
        setTimeout(function () {
            window.close();
        }, 100);
    };

    window.onload = function () {
        setTimeout(function () {
            window.focus();
            window.print();
        }, 250);
    };
<\/script>
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };


    /*
    |--------------------------------------------------------------------------
    | COMPLETE CONSULTATION
    |--------------------------------------------------------------------------
    */

    const completeConsultation = async () => {
        if (!visitInfoSaved) {
            const saved = await saveVisitInfo();
            if (!saved) return;
        }

        const printableAdditionalPrescriptions =
            getPrintableAdditionalPrescriptions();

        const procedurePayload =
            getProcedurePayload();

        const invalidProcedure =
            procedurePayload.find(
                item =>
                    !item.total_days ||
                    item.total_days < 1 ||
                    item.total_days > 365 ||
                    !item.procedure_details ||
                    !item.start_date
            );

        if (invalidProcedure) {
            await Swal.fire({
                icon: "warning",
                title: "Incomplete Procedure",
                text:
                    "Every procedure row must have Number of Days, Procedure / Instruction and Expected Start Date."
            });
            return;
        }

        if (
            !prescription.trim() &&
            !doctorNotes.trim() &&
            printableAdditionalPrescriptions.length === 0
        ) {

            const confirm = await Swal.fire({
                icon: "warning",
                title: "No Consultation Details",
                text:
                    "Prescription and doctor notes are both empty. Complete anyway?",
                showCancelButton: true,
                confirmButtonText: "Complete",
                cancelButtonText: "Cancel"
            });

            if (!confirm.isConfirmed) {
                return;
            }
        }


        try {

            setSavingConsultation(true);


            const data = new FormData();


            data.append(
                "appointment_id",
                String(patient.appointment_id)
            );

            data.append(
                "patient_id",
                String(patient.patient_id)
            );

            data.append(
                "doctor_notes",
                doctorNotes || ""
            );

            data.append(
                "prescription",
                prescription || ""
            );

            data.append(
                "tests",
                tests || ""
            );

            /*
            Legacy visits.injections is retained for old records.
            New follow-up work is stored in procedure_plans/procedure_sessions.
            */
            data.append(
                "injections",
                ""
            );

            data.append(
                "procedures",
                JSON.stringify(
                    procedurePayload
                )
            );

            data.append(
                "created_by",
                localStorage.getItem("userName") ||
                localStorage.getItem("username") ||
                localStorage.getItem("userRole") ||
                "Doctor"
            );

            data.append(
                "remarks",
                remarks || ""
            );

            data.append(
                "additional_prescriptions",
                JSON.stringify(
                    printableAdditionalPrescriptions.map(
                        (item, index) => ({
                            title:
                                item.title ||
                                `Additional Prescription ${index + 1}`,
                            content: item.content,
                            sort_order: index
                        })
                    )
                )
            );


            console.log(
                "Completing consultation:",
                {
                    appointment_id: patient.appointment_id,
                    patient_id: patient.patient_id,
                    mr_no: patient.mr_no,
                    name: patient.name,
                    additional_prescriptions:
                        printableAdditionalPrescriptions.length,
                    procedures:
                        procedurePayload.length
                }
            );


            /*
            --------------------------------------------------------
            SAVE DYNAMIC SEPARATE A5 PRESCRIPTIONS FIRST
            --------------------------------------------------------
            */

            const extraResponse = await fetch(
                `${API_BASE}save_additional_prescriptions.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        appointment_id:
                            Number(patient.appointment_id),
                        patient_id:
                            Number(patient.patient_id),
                        additional_prescriptions:
                            printableAdditionalPrescriptions.map(
                                (item, index) => ({
                                    title:
                                        item.title ||
                                        `Additional Prescription ${index + 1}`,
                                    content:
                                        item.content,
                                    sort_order:
                                        index
                                })
                            )
                    })
                }
            );

            const extraText =
                await extraResponse.text();

            if (!extraText.trim()) {
                throw new Error(
                    `Additional prescription API returned an empty response. HTTP ${extraResponse.status}`
                );
            }

            let extraResult;

            try {
                extraResult =
                    JSON.parse(extraText);
            } catch {
                throw new Error(
                    `Additional prescription API returned invalid JSON. Response: ${extraText.substring(0, 400)}`
                );
            }

            if (
                !extraResponse.ok ||
                extraResult.status !== "success"
            ) {
                throw new Error(
                    extraResult.msg ||
                    "Unable to save separate A5 prescriptions."
                );
            }


            const response = await fetch(
                `${API_BASE}save_consultation.php`,
                {
                    method: "POST",
                    body: data
                }
            );


            /*
            --------------------------------------------------------
            READ RESPONSE AS TEXT FIRST
            --------------------------------------------------------
            */

            const text =
                await response.text();


            console.log(
                "save_consultation.php HTTP:",
                response.status
            );

            console.log(
                "save_consultation.php RESPONSE:",
                text
            );


            /*
            --------------------------------------------------------
            EMPTY RESPONSE
            --------------------------------------------------------
            */

            if (!text.trim()) {

                throw new Error(
                    `Server returned an empty response. HTTP ${response.status}`
                );

            }


            /*
            --------------------------------------------------------
            PARSE JSON
            --------------------------------------------------------
            */

            let result;

            try {

                result =
                    JSON.parse(text);

            } catch (jsonError) {

                console.error(
                    "Invalid JSON from save_consultation.php:",
                    text
                );

                throw new Error(
                    `Server returned invalid JSON. HTTP ${response.status}. Response: ${text.substring(0, 500)}`
                );

            }


            /*
            --------------------------------------------------------
            HTTP ERROR
            --------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    result.msg ||
                    `Server returned HTTP ${response.status}`
                );

            }


            /*
            --------------------------------------------------------
            SUCCESS
            --------------------------------------------------------
            */

            if (
                result.status === "success"
            ) {

                const printablePageCount =
                    (prescription.trim() ? 1 : 0) +
                    printableAdditionalPrescriptions.length;

                if (printablePageCount > 0) {

                    const printChoice = await Swal.fire({
                        icon: "success",
                        title: "Consultation Completed",

                        html:
                            `Saved successfully.<br><br>` +
                            `<strong>${printablePageCount}</strong> A5 page(s) are ready.<br>` +
                            `Each prescription / referral will print on a separate A5 page.`,

                        showCancelButton: true,

                        confirmButtonText:
                            printablePageCount > 1
                                ? "Yes, Print All A5 Pages"
                                : "Yes, Print A5",

                        cancelButtonText: "No",

                        confirmButtonColor: "#198754",
                        cancelButtonColor: "#6c757d",

                        allowOutsideClick: false,
                        allowEscapeKey: false,

                        customClass: {
                            container: "doctor-swal-top"
                        }
                    });

                    if (printChoice.isConfirmed) {
                        printCurrentPrescription("all");
                    }

                } else {

                    await Swal.fire({
                        icon: "success",
                        title: "Consultation Completed",
                        html:
                            `Consultation saved successfully.<br><strong>Consultation Time:</strong> ${result.visit_date_india || "-"}`,
                        timer: 1800,
                        showConfirmButton: false
                    });

                }

                onClose();
                return;

            }


            /*
            --------------------------------------------------------
            API ERROR
            --------------------------------------------------------
            */

            throw new Error(
                result.msg ||
                "Unable to complete consultation."
            );


        } catch (error) {

            console.error(
                "Complete consultation error:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Unable to Complete",
                text:
                    error.message ||
                    "Unable to complete consultation.",
                zIndex: 999999
            });


        } finally {

            setSavingConsultation(false);

        }

    };




    /*
    |--------------------------------------------------------------------------
    | PREVIOUS VISIT PRESCRIPTION / A5 PRINT
    |--------------------------------------------------------------------------
    */

    const openVisitPrescription = async (visit) => {
        if (!visit?.id) return;

        try {
            Swal.fire({
                title: "Loading Prescription...",
                text: "Please wait",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            const response = await fetch(
                `${API_BASE}get_visit_details.php?visit_id=${encodeURIComponent(visit.id)}`
            );

            const responseText = await response.text();
            if (!responseText.trim()) {
                throw new Error(`Empty response. HTTP ${response.status}`);
            }

            const result = JSON.parse(responseText);
            if (!response.ok || result.status !== "success") {
                throw new Error(result.msg || result.message || "Unable to load prescription.");
            }

            Swal.close();

            const data = result.data || {};
            setVisitPrescriptionData({
                ...data,
                mr_no: data.mr_no || patient.mr_no || "",
                name: data.name || patient.name || "",
                phone: data.phone || patient.phone || "",
                age: data.age || patient.age || "",
                sex: data.sex || patient.sex || ""
            });
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: "error",
                title: "Unable to Load Prescription",
                text: error.message || "Unable to load prescription."
            });
        }
    };


    const printPreviousVisitPrescription = (pageSelection = "all") => {
        if (!visitPrescriptionData) return;

        const visitData = visitPrescriptionData;
        const pages = [];

        const mainPrescription = String(visitData.prescription || "").trim();
        if (mainPrescription) {
            pages.push({
                key: "main",
                title: "Prescription",
                content: mainPrescription
            });
        }

        const extraPages = Array.isArray(visitData.additional_prescriptions)
            ? visitData.additional_prescriptions
            : [];

        extraPages.forEach((item, index) => {
            const content = String(item.content || "").trim();
            if (!content) return;

            pages.push({
                key: `extra:${item.id || index}`,
                title:
                    String(item.title || "").trim() ||
                    `Additional Prescription ${index + 1}`,
                content
            });
        });

        const pagesToPrint =
            pageSelection === "all"
                ? pages
                : pages.filter(page => page.key === pageSelection);

        if (!pagesToPrint.length) {
            Swal.fire({
                icon: "info",
                title: "Nothing to Print",
                text: "No printable prescription page was found for this visit."
            });
            return;
        }

        const printWindow = window.open("", "_blank", "width=800,height=900");
        if (!printWindow) {
            Swal.fire({
                icon: "warning",
                title: "Popup Blocked",
                text: "Please allow popups in your browser to print the prescription."
            });
            return;
        }

        const sheets = pagesToPrint.map(page => `
            <section class="a5-page">
                <div class="prescription-header">
                    <h1>Kolath Medical Centre</h1>
                    <p>${escapePrintHtml(page.title)}</p>
                </div>

                <div class="patient-grid">
                    <div><strong>MR ID:</strong> ${escapePrintHtml(visitData.mr_no || "-")}</div>
                    <div><strong>Date:</strong> ${escapePrintHtml(formatDateTime(visitData.visit_date))}</div>
                    <div><strong>Name:</strong> ${escapePrintHtml(visitData.name || "-")}</div>
                    <div><strong>Phone:</strong> ${escapePrintHtml(visitData.phone || "-")}</div>
                    <div><strong>Age:</strong> ${escapePrintHtml(visitData.age || "-")}</div>
                    <div><strong>Gender:</strong> ${escapePrintHtml(visitData.sex || "-")}</div>
                </div>

                <div class="page-title">${escapePrintHtml(page.title)}</div>
                <div class="prescription-content">${formatPrescriptionForPrint(page.content)}</div>
                <div class="doctor-signature">Doctor Signature</div>
            </section>
        `).join("");

        const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Prescription - ${escapePrintHtml(visitData.mr_no || "")}</title>
<style>
@page { size: A5 portrait; margin: 10mm; }
* { box-sizing: border-box; }
html, body { margin:0; padding:0; background:#fff; color:#111; font-family:Arial,Helvetica,sans-serif; }
.a5-page { width:128mm; min-height:190mm; margin:0 auto; padding:2mm 3mm; }
.a5-page:not(:last-child) { page-break-after:always; break-after:page; }
.prescription-header { text-align:center; border-bottom:2px solid #222; padding-bottom:8px; margin-bottom:10px; }
.prescription-header h1 { margin:0; font-size:20px; }
.prescription-header p { margin:4px 0 0; font-size:12px; font-weight:700; }
.patient-grid { display:grid; grid-template-columns:1fr 1fr; gap:5px 14px; font-size:12px; border-bottom:1px solid #ccc; padding-bottom:9px; margin-bottom:12px; }
.page-title { margin:12px 0 14px; padding-bottom:6px; text-align:center; font-size:15px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; border-bottom:1px solid #bbb; }
.prescription-content { min-height:100mm; font-size:13px; line-height:1.55; }
.medicine-table-wrap { width:100%; margin-top:4px; }
.medicine-table { width:100%; border-collapse:collapse; table-layout:fixed; border:none; }
.medicine-table th, .medicine-table td { border:none !important; padding:6px 5px; text-align:left; vertical-align:top; word-break:break-word; }
.medicine-table thead th { padding-top:2px; padding-bottom:7px; font-size:11px; font-weight:700; text-transform:uppercase; color:#444; }
.medicine-table tbody td { font-size:13px; line-height:1.45; }
.medicine-col { width:52%; }
.duration-col { width:22%; }
.dosage-col { width:26%; }
.medicine-name { font-weight:600; padding-left:0 !important; }
.dosage-text { white-space:nowrap; font-weight:600; letter-spacing:.2px; }
.prescription-notes { margin-top:14px; padding-top:8px; border-top:1px solid #ddd; }
.prescription-note { margin:4px 0; font-size:12.5px; line-height:1.5; }
.doctor-signature { margin-top:15mm; text-align:right; font-size:11px; }
@media print { .a5-page { width:auto; min-height:auto; margin:0; } }
</style>
</head>
<body>
${sheets}
<script>
window.onafterprint=function(){
    setTimeout(function(){
        window.close();
    },100);
};
window.onload=function(){
    setTimeout(function(){
        window.focus();
        window.print();
    },250);
};
<\/script>
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };


    /*
    |--------------------------------------------------------------------------
    | DOCUMENT URL
    |--------------------------------------------------------------------------
    */

    const documentUrl =
        (path) =>
            `${API_BASE}${path}`;


    return (

        <div
            className="doctor-modal-overlay"
        >

            <div
                className="doctor-patient-modal"
            >

                {/* HEADER */}

                <div className="doctor-modal-header">

                    <div>

                        <h3 className="white">

                            <i className="fa fa-user-md"></i>

                            &nbsp;
                            {patient.name}

                        </h3>

                        <span className="white">

                            MR ID:
                            {" "}
                            {patient.mr_no || "-"}

                            &nbsp; | &nbsp;

                            Phone:
                            {" "}
                            {patient.phone || "-"}

                        </span>

                    </div>


                    <button
                        type="button"
                        onClick={onClose}
                        className="doctor-modal-close"
                    >

                        <i className="fa fa-times"></i>

                    </button>

                </div>


                <div className="doctor-modal-content">

                    {/* CURRENT VISIT */}

                    <section className="doctor-card" style={{ background: 'white', border: '1px solid #ccc' }}>

                        <div className="doctor-card-title" style={{ color: 'black' }}>

                            <i className="fa fa-stethoscope"></i>

                            Current Visit

                        </div>


                        <div className="current-purpose">

                            {patient.priority ===
                                "emergency" && (

                                    <span className="emergency-label">

                                        <i className="fa fa-exclamation-triangle"></i>

                                        EMERGENCY

                                    </span>

                                )}

                            <strong>
                                Purpose
                            </strong>

                            <textarea className="form-control" rows={2}
                                aria-label="Purpose of Visit" value={visitInfo.purpose}
                                onChange={e => changeVisitInfo("purpose", e.target.value)} />
                            <div className="medical-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))", gap: 12, marginTop: 12 }}>
                                {[
                                    ["bp", "BP", "e.g. 120/80"],
                                    ["sugar", "Sugar", "e.g. 110 mg/dL"],
                                    ["height", "Height (cm)", "e.g. 165"],
                                    ["weight", "Weight (kg)", "e.g. 65"],
                                    ["temperature", "Body Temperature (°F)", "e.g. 98.6"]
                                ].map(([key, label, placeholder]) => (
                                    <label key={key}>{label}
                                        <input type="text" maxLength={100} className="form-control"
                                            value={visitInfo[key]} placeholder={placeholder}
                                            onChange={e => changeVisitInfo(key, e.target.value)} />
                                    </label>
                                ))}
                            </div>
                            <button type="button" className="history-update" style={{ marginTop: 12 }}
                                onClick={saveVisitInfo} disabled={savingVisitInfo || visitInfoSaved}>
                                {savingVisitInfo ? "Saving..." : visitInfoSaved ? "Visit Details Saved" : "Save Purpose & Vitals"}
                            </button>

                        </div>


                        {patient.priority ===
                            "emergency" && (

                                <div className="casualty-info">

                                    <strong>
                                        Casualty Details
                                    </strong>

                                    <p>
                                        {patient.casualty_details ||
                                            "No details entered."}
                                    </p>


                                    <strong>
                                        Medicine Given
                                    </strong>

                                    <p>
                                        {patient.casualty_medicine ||
                                            "No medicine details entered."}
                                    </p>

                                </div>

                            )}

                    </section>


                    {/* EXISTING / PENDING PROCEDURES */}

                    {/* <section
                        className="doctor-card"
                        style={{
                            background: "white",
                            border: "1px solid #d5dddf"
                        }}
                    >
                        <PatientProcedureHistory
                            patientId={patient.patient_id}
                            heading="Procedure History / Pending Procedures"
                            canEdit={true}
                            canComplete={true}
                        />
                    </section> */}


                    {/* PATIENT HISTORY */}

                    <section className="doctor-card" style={{ background: 'white', border: '1px solid gray' }}>

                        <div className="doctor-card-title" style={{ color: 'black' }}>

                            <i className="fa fa-heartbeat"></i>

                            Patient History

                        </div>


                        <div className="history-details">
                            <div className="history-detail-heading">
                                <strong style={{ background: 'white', padding: '10px', borderRadius: '6px' }}>Other Medical History / Other Difficulties</strong>
                            </div>

                            <textarea
                                className="single-medical-history-textarea"
                                value={medicalHistory}
                                onChange={e => setMedicalHistory(e.target.value)}
                                placeholder="Enter Medical History / Other Difficulties..."
                                rows="6"
                            />
                        </div>


                        <button
                            type="button"
                            className="history-update"
                            onClick={
                                saveHistory
                            }
                            disabled={
                                savingHistory
                            }
                        >

                            <i
                                className={
                                    savingHistory
                                        ? "fa fa-spinner fa-spin"
                                        : "fa fa-save"
                                }
                            ></i>&nbsp;&nbsp;

                            {savingHistory
                                ? "Updating..."
                                : "Update Patient History"}

                        </button>

                    </section>


                    {/* CURRENT CONSULTATION */}

                    <section className="doctor-card" style={{ background: '#013638' }}>

                        <div className="doctor-card-title white">

                            <i className="fa fa-pencil"></i>

                            Current Consultation

                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <DoctorTextarea
                                    label="Doctor Notes"
                                    value={
                                        doctorNotes
                                    }
                                    onChange={
                                        setDoctorNotes
                                    }
                                    placeholder="Enter today's clinical findings..." 
                                />
                            </div>
                            <div className="col-md-6" style={{ background: '#006262', padding: '10px', borderRadius: '6px' }}> 
                                <DoctorTextarea
                                    label="Prescription"
                                    value={
                                        prescription
                                    }
                                    onChange={
                                        setPrescription
                                    }
                                    placeholder="Enter prescription..."
                                />
                            </div>

                        </div>






                        <div
                            style={{
                                marginTop: "18px",
                                marginBottom: "18px",
                                padding: "16px",
                                border: "1px solid #d9e2ec",
                                borderRadius: "12px",
                                background: "#f8fbff"
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginBottom: "12px",
                                    flexWrap: "wrap"
                                }}
                            >

                                <div>
                                    <strong>
                                        Separate A5 Test / Referral Prescriptions
                                    </strong>

                                    <div
                                        style={{
                                            marginTop: "4px",
                                            fontSize: "12px",
                                            color: "#667085"
                                        }}
                                    >
                                        Add MRI, Lab, Scan, X-Ray or any other
                                        separate prescription. Each item prints
                                        on its own A5 page.
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="history-update"
                                    onClick={
                                        addAdditionalPrescription
                                    }
                                    style={{
                                        width: "auto",
                                        margin: 0
                                    }}
                                >
                                    <i className="fa fa-plus"></i>
                                    &nbsp; Add Separate A5 Page
                                </button>

                            </div>


                            {additionalPrescriptions.length === 0 ? (

                                <div
                                    style={{
                                        padding: "14px",
                                        border: "1px dashed #b8c4d1",
                                        borderRadius: "10px",
                                        color: "#667085",
                                        fontSize: "13px",
                                        background: "#fff"
                                    }}
                                >
                                    No additional prescription added.
                                </div>

                            ) : (

                                additionalPrescriptions.map(
                                    (item, index) => (

                                        <div
                                            key={item.id}
                                            style={{
                                                marginTop:
                                                    index === 0
                                                        ? "0"
                                                        : "12px",
                                                padding: "14px",
                                                border: "1px solid #cfd8e3",
                                                borderRadius: "10px",
                                                background: "#fff"
                                            }}
                                        >

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    marginBottom: "10px"
                                                }}
                                            >
                                                <strong>
                                                    Additional A5 Page {index + 1}
                                                </strong>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeAdditionalPrescription(
                                                            item.id
                                                        )
                                                    }
                                                    style={{
                                                        border: "none",
                                                        background: "#fff1f2",
                                                        color: "#b42318",
                                                        borderRadius: "8px",
                                                        padding: "7px 10px",
                                                        cursor: "pointer"
                                                    }}
                                                    title="Remove"
                                                >
                                                    <i className="fa fa-trash"></i>
                                                    &nbsp; Remove
                                                </button>
                                            </div>


                                            <input
                                                type="text"
                                                value={
                                                    item.title
                                                }
                                                onChange={
                                                    e =>
                                                        updateAdditionalPrescription(
                                                            item.id,
                                                            "title",
                                                            e.target.value
                                                        )
                                                }
                                                placeholder="Title - e.g. MRI Scan / Lab Tests / X-Ray"
                                                style={{
                                                    width: "100%",
                                                    marginBottom: "10px",
                                                    padding: "10px 12px",
                                                    border: "1px solid #cfd8e3",
                                                    borderRadius: "8px",
                                                    fontSize: "14px"
                                                }}
                                            />


                                            <textarea
                                                value={
                                                    item.content
                                                }
                                                onChange={
                                                    e =>
                                                        updateAdditionalPrescription(
                                                            item.id,
                                                            "content",
                                                            e.target.value
                                                        )
                                                }
                                                placeholder="Enter the separate test / referral prescription..."
                                                rows="5"
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    border: "1px solid #cfd8e3",
                                                    borderRadius: "8px",
                                                    fontSize: "14px",
                                                    resize: "vertical"
                                                }}
                                            />

                                        </div>

                                    )
                                )

                            )}

                        </div>


                        <DoctorTextarea
                            label="Tests / Check-up"
                            value={
                                tests
                            }
                            onChange={
                                setTests
                            }
                            placeholder="Blood test, urine test, X-Ray, scan, etc."
                        />


                        {/* PROCEDURE / FOLLOW-UP PLAN */}

                        <div className="doctor-procedure-builder">

                            <div className="doctor-procedure-builder-header">

                                <div>
                                    <strong>
                                        <i className="fa fa-medkit"></i>
                                        &nbsp; Procedure / Follow-up Plan
                                    </strong>

                                    <div
                                        style={{
                                            fontSize: "12px",
                                            marginTop: "4px",
                                            color: "#526667"
                                        }}
                                    >
                                        Use this for injection, dressing or any procedure
                                        that can be completed without another doctor consultation.
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="procedure-btn complete"
                                    onClick={addProcedurePlan}
                                >
                                    <i className="fa fa-plus"></i>
                                    &nbsp; Add Procedure
                                </button>

                            </div>


                            {procedurePlans.length === 0 ? (

                                <div
                                    style={{
                                        padding: "12px",
                                        background: "white",
                                        borderRadius: "8px",
                                        color: "#6a7778",
                                        fontSize: "13px"
                                    }}
                                >
                                    No follow-up procedure added for this consultation.
                                </div>

                            ) : (

                                procedurePlans.map(
                                    (item, index) => (

                                        <div
                                            className="doctor-procedure-row"
                                            key={item.id}
                                        >

                                            <div>
                                                <label>
                                                    No. of Days
                                                </label>

                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="365"
                                                    value={item.total_days}
                                                    onChange={
                                                        e =>
                                                            updateProcedurePlan(
                                                                item.id,
                                                                "total_days",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="3"
                                                />
                                            </div>


                                            <div>
                                                <label>
                                                    Expected Start Date
                                                </label>

                                                <input
                                                    type="date"
                                                    value={item.start_date}
                                                    onChange={
                                                        e =>
                                                            updateProcedurePlan(
                                                                item.id,
                                                                "start_date",
                                                                e.target.value
                                                            )
                                                    }
                                                />
                                            </div>


                                            <div>
                                                <label>
                                                    Procedure / Instruction
                                                </label>

                                                <textarea
                                                    rows="3"
                                                    value={item.procedure_details}
                                                    onChange={
                                                        e =>
                                                            updateProcedurePlan(
                                                                item.id,
                                                                "procedure_details",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="Example: Inj. Ceftriaxone IM once daily / wound dressing and observation..."
                                                />
                                            </div>


                                            <div>
                                                <label>
                                                    Action
                                                </label>

                                                <button
                                                    type="button"
                                                    className="procedure-btn danger"
                                                    onClick={() =>
                                                        removeProcedurePlan(
                                                            item.id
                                                        )
                                                    }
                                                >
                                                    <i className="fa fa-trash"></i>
                                                    &nbsp; Remove
                                                </button>
                                            </div>

                                        </div>

                                    )
                                )

                            )}

                        </div>


                        <DoctorTextarea
                            label="Other Remarks"
                            value={
                                remarks
                            }
                            onChange={
                                setRemarks
                            }
                            placeholder="Additional consultation details..."
                        />

                    </section>


                    {/* DOCUMENTS */}
                    <section className="doctor-card">
                        <MedicalDocumentManager
                            patientId={patient.patient_id}
                            appointmentId={patient.appointment_id || ""}
                            heading="Medical Documents"
                        />
                    </section>

                    {/* VISIT HISTORY - SAME UI AS PatientHistorySearch */}
                    <section className="doctor-card ph-card" style={{ background: "white" }}>
                        <div className="ph-card-heading">
                            <div style={{ color: 'black' }}>
                                <i className="fa fa-history"></i> Visit History
                            </div>
                            <span className="ph-visit-count">
                                {visits.length} Visit{visits.length === 1 ? "" : "s"}
                            </span>
                        </div>

                        {visits.length ? (
                            <div className="ph-visits">
                                {visits.map((visit, index) => {
                                    const open = expandedVisit === visit.id;

                                    return (
                                        <div
                                            className={open ? "ph-visit open" : "ph-visit"} style={{ color: "gray" }}
                                            key={visit.id}
                                        >
                                            <div
                                                className="ph-visit-header"
                                                onClick={() =>
                                                    setExpandedVisit(open ? null : visit.id)
                                                }
                                            >
                                                <div className="ph-visit-number">
                                                    {visits.length - index}
                                                </div>

                                                <div className="ph-visit-title">
                                                    <strong>
                                                        {formatDateTime(visit.visit_date)}
                                                    </strong>
                                                    <span>
                                                        {visit.prescription
                                                            ? truncateDoctorVisitText(visit.prescription, 70)
                                                            : visit.doctor_notes
                                                                ? truncateDoctorVisitText(visit.doctor_notes, 70)
                                                                : "Consultation"}
                                                    </span>
                                                </div>

                                                <div className="ph-visit-status">
                                                    <span>
                                                        {visit.visit_status || "completed"}
                                                    </span>
                                                    <i
                                                        className={
                                                            open
                                                                ? "fa fa-chevron-up"
                                                                : "fa fa-chevron-down"
                                                        }
                                                    ></i>
                                                </div>
                                            </div>

                                            {open && (
                                                <div className="ph-visit-body">
                                                    <div className="ph-visit-vitals">
                                                        <DoctorVisitMiniInfo
                                                            label="BP"
                                                            value={visit.bp}
                                                        />
                                                        <DoctorVisitMiniInfo
                                                            label="Sugar"
                                                            value={visit.sugar}
                                                        />
                                                        <DoctorVisitMiniInfo
                                                            label="Height"
                                                            value={
                                                                visit.height
                                                                    ? `${visit.height} cm`
                                                                    : "-"
                                                            }
                                                        />
                                                        <DoctorVisitMiniInfo
                                                            label="Weight"
                                                            value={
                                                                visit.weight
                                                                    ? `${visit.weight} kg`
                                                                    : "-"
                                                            }
                                                        />
                                                        <DoctorVisitMiniInfo
                                                            label="Temperature"
                                                            value={visit.temperature ? `${visit.temperature} °F` : "-"}
                                                        />
                                                    </div>

                                                    <DoctorVisitField
                                                        label="Doctor Notes"
                                                        icon="fa fa-user-md"
                                                        value={visit.doctor_notes}
                                                    />
                                                    <DoctorVisitField
                                                        label="Prescription"
                                                        icon="fa fa-medkit"
                                                        value={visit.prescription}
                                                    />
                                                    <DoctorVisitField
                                                        label="Tests / Check-up"
                                                        icon="fa fa-flask"
                                                        value={visit.tests}
                                                    />
                                                    <DoctorVisitField
                                                        label="Injection (Legacy)"
                                                        icon="fa fa-eyedropper"
                                                        value={visit.injections}
                                                    />
                                                    <DoctorVisitField
                                                        label="Remarks"
                                                        icon="fa fa-comment-o"
                                                        value={visit.remarks}
                                                    />

                                                    <div
                                                        style={{
                                                            marginTop: "14px",
                                                            textAlign: "right"
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="ph-btn ph-btn-primary"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                openVisitPrescription(visit);
                                                            }}
                                                        >
                                                            <i className="fa fa-print"></i>{" "}
                                                            Prescription / Print A5
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="ph-empty-history">
                                <i className="fa fa-history"></i>
                                <h4>No Previous Visits</h4>
                                <p>Consultation history will appear here.</p>
                            </div>
                        )}
                    </section>

                    {/* Procedure Details */}
                    <section
                        className="doctor-card"
                        style={{
                            background: "white",
                            border: "1px solid #d5dddf"
                        }}
                    >
                        <PatientProcedureHistory
                            patientId={patient.patient_id}
                            heading="Procedure History / Pending Procedures"
                            canEdit={true}
                            canComplete={true}
                        />
                    </section>

                </div>


                {/* FOOTER */}

                <div className="doctor-modal-footer">

                    <button
                        type="button"
                        className="doctor-close-button"
                        onClick={
                            onClose
                        }
                    >

                        Close

                    </button>


                    <button
                        type="button"
                        className="complete-consultation"
                        onClick={
                            completeConsultation
                        }
                        disabled={
                            savingConsultation
                        }
                    >

                        <i
                            className={
                                savingConsultation
                                    ? "fa fa-spinner fa-spin"
                                    : "fa fa-check"
                            }
                        ></i>
                            &nbsp;
                        {savingConsultation
                            ? "Completing..."
                            : "Complete Consultation"}

                    </button>

                </div>

            </div>


            {/* DOCUMENT PREVIEW */}

            {previewDocument && (

                <DocumentPreview
                    document={
                        previewDocument
                    }
                    onClose={() =>
                        setPreviewDocument(
                            null
                        )
                    }
                />

            )}


            {/* PREVIOUS VISIT PRESCRIPTION */}
            {visitPrescriptionData && (
                <PreviousVisitPrescriptionModal
                    data={visitPrescriptionData}
                    onClose={() => setVisitPrescriptionData(null)}
                    onPrint={printPreviousVisitPrescription}
                />
            )}

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| MEDICAL INPUT
|--------------------------------------------------------------------------
*/

function MedicalInput({
    label,
    value,
    onChange,
    placeholder
}) {

    return (

        <div className="medical-input">

            <label>
                {label}
            </label>

            <input
                type="text"
                value={
                    value || ""
                }
                onChange={
                    e =>
                        onChange(
                            e.target.value
                        )
                }
                placeholder={
                    placeholder
                }
            />

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| DOCTOR TEXTAREA
|--------------------------------------------------------------------------
*/

function DoctorTextarea({
    label,
    value,
    onChange,
    placeholder
}) {

    return (

        <div className="doctor-textarea">

            <label>
                {label}
            </label>

            <textarea
                value={
                    value
                }
                onChange={
                    e =>
                        onChange(
                            e.target.value
                        )
                }
                placeholder={
                    placeholder
                }
            />

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| DOCUMENT PREVIEW
|--------------------------------------------------------------------------
*/

function DocumentPreview({
    document,
    onClose
}) {

    const url =
        `${API_BASE}${document.document_path}`;

    const isImage =
        /\.(jpg|jpeg|png)$/i.test(
            document.document_name
        );

    const isPdf =
        /\.pdf$/i.test(
            document.document_name
        );


    return (

        <div
            className="document-preview-overlay"
            onClick={onClose}
        >

            <div
                className="document-preview"
                onClick={
                    e =>
                        e.stopPropagation()
                }
            >

                <div className="document-preview-header">

                    <strong>
                        {document.document_name}
                    </strong>

                    <button
                        type="button"
                        onClick={onClose}
                    >

                        <i className="fa fa-times"></i>

                    </button>

                </div>


                <div className="document-preview-body">

                    {isImage ? (

                        <img
                            src={url}
                            alt={
                                document.document_name
                            }
                        />

                    ) : isPdf ? (

                        <iframe
                            src={url}
                            title={
                                document.document_name
                            }
                        />

                    ) : (

                        <div className="document-file-preview">

                            <i className="fa fa-file-text-o"></i>

                            <p>
                                {document.document_name}
                            </p>

                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open Document
                            </a>

                        </div>

                    )}

                </div>

            </div>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| SHARED-STYLE VISIT HISTORY HELPERS
| Same class names / layout as PatientHistorySearch
|--------------------------------------------------------------------------
*/

function DoctorVisitMiniInfo({ label, value }) {
    return (
        <div className="ph-mini-info">
            <small>{label}</small>
            <strong>{value || "-"}</strong>
        </div>
    );
}

function DoctorVisitField({ label, icon, value }) {
    return (
        <div className="ph-visit-field">
            <div className="ph-visit-field-title">
                <i className={icon}></i> {label}
            </div>
            <div className="ph-visit-field-value">
                {value || "-"}
            </div>
        </div>
    );
}

function truncateDoctorVisitText(value, length) {
    if (!value) return "";
    const text = String(value);
    return text.length <= length
        ? text
        : `${text.substring(0, length)}...`;
}

function PreviousVisitPrescriptionModal({ data, onClose, onPrint }) {
    const extraPages = Array.isArray(data.additional_prescriptions)
        ? data.additional_prescriptions
        : [];

    const hasMainPrescription = Boolean(
        String(data.prescription || "").trim()
    );

    const printableExtras = extraPages.filter(item =>
        String(item.content || "").trim()
    );

    const printableCount =
        (hasMainPrescription ? 1 : 0) + printableExtras.length;

    return (
        <div className="ph-modal-overlay" onMouseDown={onClose}>
            <div
                className="ph-modal"
                onMouseDown={event => event.stopPropagation()}
                style={{ maxWidth: "850px", width: "94vw" }}
            >
                <div className="ph-modal-header">
                    <div>
                        <h3>
                            <i className="fa fa-file-text-o"></i>{" "}
                            Previous Visit Prescription
                        </h3>
                        <small>
                            {data.name || "-"} · MR {data.mr_no || "-"} ·{" "}
                            {formatDateTime(data.visit_date)}
                        </small>
                    </div>

                    <button type="button" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <div
                    style={{
                        padding: "18px",
                        maxHeight: "70vh",
                        overflowY: "auto"
                    }}
                >
                    <DoctorVisitField
                        label="Doctor Notes"
                        icon="fa fa-user-md"
                        value={data.doctor_notes}
                    />
                    <DoctorVisitField
                        label="Prescription"
                        icon="fa fa-medkit"
                        value={data.prescription}
                    />

                    {hasMainPrescription && (
                        <div style={{ marginTop: "8px", textAlign: "right" }}>
                            <button
                                type="button"
                                className="ph-btn ph-btn-primary"
                                onClick={() => onPrint("main")}
                            >
                                <i className="fa fa-print"></i>{" "}
                                Print Main Prescription A5
                            </button>
                        </div>
                    )}

                    <DoctorVisitField
                        label="Tests / Check-up"
                        icon="fa fa-flask"
                        value={data.tests}
                    />

                    {extraPages.length > 0 && (
                        <div style={{ marginTop: "18px" }}>
                            <h4 style={{ margin: "0 0 8px" }}>
                                Separate A5 Test / Referral Prescriptions
                            </h4>

                            {extraPages.map((item, index) => {
                                const key = `extra:${item.id || index}`;
                                const content = String(item.content || "").trim();

                                return (
                                    <div
                                        key={item.id || `${item.title}-${index}`}
                                        style={{
                                            marginTop: "10px",
                                            padding: "12px",
                                            border: "1px solid #d7dee8",
                                            borderRadius: "10px",
                                            background: "#f8fbff"
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: "12px",
                                                flexWrap: "wrap"
                                            }}
                                        >
                                            <div style={{ flex: "1 1 320px" }}>
                                                <strong>
                                                    {item.title ||
                                                        `Additional Prescription ${index + 1}`}
                                                </strong>
                                                <div
                                                    style={{
                                                        marginTop: "7px",
                                                        whiteSpace: "pre-wrap",
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {item.content || "-"}
                                                </div>
                                            </div>

                                            {content && (
                                                <button
                                                    type="button"
                                                    className="ph-btn ph-btn-primary"
                                                    onClick={() => onPrint(key)}
                                                >
                                                    <i className="fa fa-print"></i>{" "}
                                                    Print This A5
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <DoctorVisitField
                        label="Injection (Legacy)"
                        icon="fa fa-eyedropper"
                        value={data.injections}
                    />
                    <DoctorVisitField
                        label="Remarks"
                        icon="fa fa-comment-o"
                        value={data.remarks}
                    />
                </div>

                <div
                    className="ph-modal-actions"
                    style={{ padding: "0 18px 18px" }}
                >
                    <button
                        type="button"
                        className="ph-btn ph-btn-light"
                        onClick={onClose}
                    >
                        Close
                    </button>

                    {printableCount > 0 && (
                        <button
                            type="button"
                            className="ph-btn ph-btn-primary"
                            onClick={() => onPrint("all")}
                        >
                            <i className="fa fa-print"></i>{" "}
                            {printableCount > 1
                                ? `Print All ${printableCount} A5 Pages`
                                : "Print A5 Prescription"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

